import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { XCircle, RefreshCw, Building2 } from "lucide-react";
import PanelState, { extractApiError } from "@/components/PanelState";

// Coverage tone — same three-tier signal as Tech Goals Audit for visual consistency.
function bannerTone(attributable, total) {
  if (total === 0) return "bg-[#e6f9f2] text-[#0e5a48] border-[#c1eddc]";
  if (attributable === 0) return "bg-[#fdecec] text-[#a12b2b] border-[#f5c5c5]";
  if (attributable < total) return "bg-[#fff4d6] text-[#8a5b00] border-[#f3d97a]";
  return "bg-[#e6f9f2] text-[#0e5a48] border-[#c1eddc]";
}

export default function AdminCancelAttributionAudit() {
  const [tenantSlug, setTenantSlug] = useState("mre-swmo");
  const [tenants, setTenants] = useState([]);
  const [data, setData] = useState(null);
  const [loadState, setLoadState] = useState({ status: "loading", error: null });
  const [refreshing, setRefreshing] = useState(false);

  const loadTenants = useCallback(async () => {
    try {
      const { data } = await api.get("/tenants");
      setTenants(data);
    } catch { /* falls back to text input */ }
  }, []);

  const load = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoadState({ status: "loading", error: null });
    try {
      const { data } = await api.get(`/admin/cancel-attribution-audit?tenant_slug=${encodeURIComponent(tenantSlug)}`);
      setData(data);
      setLoadState({ status: "ok", error: null });
    } catch (err) {
      setData(null);
      setLoadState({ status: "error", error: extractApiError(err) });
    } finally {
      setRefreshing(false);
    }
  }, [tenantSlug]);

  useEffect(() => { loadTenants(); }, [loadTenants]);
  useEffect(() => { load(); }, [load]);

  if (loadState.status === "loading" && !data) {
    return (
      <div className="max-w-7xl mx-auto space-y-5" data-testid="admin-cancel-attribution-audit-page">
        <Header />
        <PanelState state="loading" />
      </div>
    );
  }
  if (loadState.status === "error") {
    return (
      <div className="max-w-7xl mx-auto space-y-5" data-testid="admin-cancel-attribution-audit-page">
        <Header />
        <PanelState state="error" error={loadState.error} onRetry={() => load()} />
      </div>
    );
  }

  const attr = data?.attribution || {};
  const kj = data?.kpi_jobs_job_id_coverage || {};

  return (
    <div className="max-w-7xl mx-auto space-y-5" data-testid="admin-cancel-attribution-audit-page">
      <Header />

      {/* Tenant picker */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-[12.5px] text-[#5d6a80]">
          <Building2 size={13} strokeWidth={1.8} />
          {tenants.length > 0 ? (
            <select
              value={tenantSlug}
              onChange={(e) => setTenantSlug(e.target.value)}
              className="border border-[#dde3ec] rounded px-2 py-1 bg-white text-[#1e2c48]"
              data-testid="tenant-picker"
            >
              {tenants.map((t) => (
                <option key={t.slug} value={t.slug}>{t.business_name} ({t.slug})</option>
              ))}
            </select>
          ) : (
            <input
              value={tenantSlug}
              onChange={(e) => setTenantSlug(e.target.value)}
              className="border border-[#dde3ec] rounded px-2 py-1 bg-white text-[#1e2c48] font-mono-data text-[12px]"
              placeholder="tenant slug"
              data-testid="tenant-picker-input"
            />
          )}
        </div>
        <button
          onClick={() => load(true)}
          disabled={refreshing}
          className="p-1.5 rounded-md hover:bg-[#f5f6f8] disabled:opacity-50"
          data-testid="cancel-attribution-refresh"
          aria-label="Refresh"
        >
          <RefreshCw size={14} strokeWidth={1.8} className={refreshing ? "animate-spin" : ""} />
        </button>
      </div>

      {/* Diagnosis banner */}
      <div
        className={`border rounded-lg p-3.5 ${bannerTone(attr.attributable_total || 0, data?.hard_cancel_count || 0)}`}
        data-testid="cancel-attribution-banner"
      >
        <div className="text-[13px] font-semibold flex items-center gap-2 flex-wrap">
          <XCircle size={16} strokeWidth={1.8} />
          Cancellation attribution — {data?.tenant_slug}
        </div>
        <div className="text-[11.5px] font-mono-data mt-1 opacity-90 flex flex-wrap gap-x-4 gap-y-1">
          <span>week_of={data?.latest_reporting_week_of || "—"}</span>
          {data?.window && (
            <span>window=[{data.window.start}, {data.window.end_exclusive})</span>
          )}
          <span>hard_cancels={data?.hard_cancel_count ?? 0}</span>
        </div>
        <div className="text-[11.5px] mt-1.5 opacity-90" data-testid="cancel-attribution-diagnosis">
          {data?.diagnosis}
        </div>
      </div>

      {/* Attribution breakdown grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <StatCard label="Layer 1 · unit field" value={attr.layer1_unit_field} accent="#0e5a48" />
        <StatCard label="Layer 2 · tech_name field" value={attr.layer2_tech_name_field} accent="#0e5a48" />
        <StatCard label="Layer 3 · job_id present" value={attr.layer3_job_id_available} sub={`join hits: ${attr.layer3_job_id_join_hits ?? 0}`} accent="#8a5b00" />
        <StatCard label="Unattributable" value={attr.unattributable} accent="#a12b2b" />
        <StatCard label="Attributable total" value={attr.attributable_total} accent="#0e5a48" bold />
        <StatCard label="Hard cancels in window" value={data?.hard_cancel_count ?? 0} accent="#33445e" bold />
      </div>

      {/* kpi_jobs.job_id coverage (the load-bearing assumption for Layer 3) */}
      <div className="border border-[#e7e9ee] rounded-lg bg-white p-4" data-testid="kpi-jobs-job-id-coverage">
        <div className="text-[10.5px] uppercase tracking-widest text-[#8090aa] font-semibold mb-2">
          kpi_jobs.job_id coverage — load-bearing for Layer 3
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MiniStat label="Total kpi_jobs (all weeks)" value={kj.total_rows_all_weeks} />
          <MiniStat label="Populated (all weeks)" value={kj.populated_all_weeks} accent={kj.populated_all_weeks > 0 ? "#0e5a48" : "#a12b2b"} />
          <MiniStat label="Populated %" value={kj.populated_pct_all_weeks != null ? `${kj.populated_pct_all_weeks}%` : "—"} accent={kj.populated_pct_all_weeks > 0 ? "#0e5a48" : "#a12b2b"} />
          <MiniStat label="This week populated" value={`${kj.populated_this_week ?? 0} / ${kj.total_rows_this_week ?? 0}`} />
        </div>
        {kj.populated_all_weeks === 0 && (
          <div className="mt-3 text-[11.5px] text-[#a12b2b] bg-[#fdecec] border border-[#f5c5c5] rounded p-2">
            <strong>kpi_jobs.job_id is unpopulated across the board.</strong> Layer 3 of the
            cancellation attribution waterfall (join by <code className="font-mono-data">job_id</code>)
            can never resolve until Sam&apos;s ingest fills this field. Cancels will stay
            unattributable regardless of the frontend/backend fix. Investigate the ingest side.
          </div>
        )}
      </div>
    </div>
  );
}

function Header() {
  return (
    <div className="space-y-1">
      <div className="text-[11.5px] uppercase tracking-widest text-[#8090aa] font-semibold">
        Platform Admin
      </div>
      <h1 className="text-2xl font-semibold text-[#1e2c48] flex items-center gap-2">
        <XCircle size={22} strokeWidth={1.8} className="text-[#a12b2b]" />
        Cancel Attribution Audit
      </h1>
      <p className="text-[13px] text-[#5d6a80]">
        Diagnostic for the L10 per-unit cancellation-rate pipeline. Shows which cancels
        resolve to a unit via each layer of the enrichment waterfall, and whether the
        load-bearing <code className="font-mono-data text-[11.5px] bg-[#f5f6f8] px-1 rounded">kpi_jobs.job_id</code>
        field is actually populated by ingest.
      </p>
    </div>
  );
}

function StatCard({ label, value, sub, accent = "#33445e", bold = false }) {
  return (
    <div className="border border-[#e7e9ee] rounded-lg bg-white p-3">
      <div className="text-[10px] uppercase tracking-widest text-[#8090aa] font-semibold">{label}</div>
      <div className={`font-mono-data ${bold ? "font-bold text-[22px]" : "font-semibold text-[18px]"} mt-1`}
        style={{ color: accent }}>
        {value ?? 0}
      </div>
      {sub && <div className="text-[10.5px] text-[#5d6a80] mt-0.5">{sub}</div>}
    </div>
  );
}

function MiniStat({ label, value, accent = "#33445e" }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[#8090aa] font-semibold">{label}</div>
      <div className="font-mono-data font-semibold text-[15px] mt-0.5" style={{ color: accent }}>
        {value ?? "—"}
      </div>
    </div>
  );
}
